import { useEffect, useMemo, useState } from "react";
import ModalContainer from "./ModalContainer";
import SingleSelect from "../singleselect/SingleSelect";
import { cn } from "../../lib/utils";
import EmptyBox from "../emptystate/EmptyBox";
import LoadingBox from "../loading/LoadingBox";

/**
 * POPUP • Applica set dati
 * Selezione del preset contenuti (raggruppato per tipologia organizzazione)
 * da applicare all'ente selezionato
 */
export default function ApplyContentPresetPopup({
	open,
	setOpen,
	organization = null,
	contentPresetsGrouped = {},
	isLoading = false,
	onConfirm = () => {},
	onCreatePreset = () => {},
}) {
	const [selectedItem, setSelectedItem] = useState(null);

	const organizationTypeId =
		organization?.organization_type_id ?? organization?.organization_type?.id ?? null;

	/**
	 * Solo i preset della tipologia organizzazione dell'ente selezionato
	 */
	const contentPresets = useMemo(() => {
		const entries = Object.entries(contentPresetsGrouped).filter(
			([key, group]) =>
				!organizationTypeId ||
				String(group?.id ?? key) === String(organizationTypeId) ||
				group?.is_master
		);

		entries.sort(([, a], [, b]) => (b?.is_master ? 1 : 0) - (a?.is_master ? 1 : 0));

		return Object.fromEntries(entries);
	}, [contentPresetsGrouped, organizationTypeId]);

	const itemsCount = Object.values(contentPresets).reduce(
		(acc, group) => acc + (group?.listItems?.length ?? 0),
		0
	);

	/**
	 * Init value
	 */
	useEffect(() => {
		if (open) setSelectedItem(null);
	}, [open]);

	return (
		<ModalContainer isOpen={open} setIsOpen={setOpen}>
			<div
				className={`main_modal_content ${!isLoading && Boolean(itemsCount) && "medium_size_modal"}`}
				id={"apply-content-preset-popup"}
			>
				<button
					className="main_close"
					onClick={() => setOpen(false)}
					type="button"
					aria-label="Chiudi"
				>
					<span className="icon_10 default close icon_mask !bg-white"></span>
				</button>

				{/* Preset • loading */}
				{isLoading && <LoadingBox blocked="true" classNames={"!min-h-[300px]"} />}

				{/* Preset • set */}
				{!isLoading && Boolean(itemsCount) && (
					<>
						{/* Modal Header */}
						<div className="main_header_modal left_text">
							<span className="title">Applica set dati</span>

							{/* Ente selezionato */}
							{organization && (
								<div className="text-[12px] pt-[12px] flex flex-col gap-1">
									<div className={"text-[14px] "}>{organization.name}</div>

									{organization?.reseller?.name && (
										<div className={"text-12 text-dark-50"}>{organization.reseller.name}</div>
									)}
								</div>
							)}
						</div>

						{/* Modal Body */}
						<div className={"main_body_modal"}>
							<div className="global_form_landing">
								<div className="base">
									<div className="wrap_input_landing">
										<div className="flex items-center justify-between gap-3">
											<span className="label_input">Preset contenuti</span>
										</div>

										<SingleSelect
											listItems={contentPresets}
											selectedItem={selectedItem}
											onSelect={setSelectedItem}
											placeholder={"Seleziona preset"}
											isLoading={isLoading}
											searchable={itemsCount > 5}
											disabled={!isLoading && !itemsCount}
										/>
									</div>
								</div>
							</div>
						</div>

						{/* Modal Footer */}
						<div className="main_footer_modal">
							<div className="right_content">
								{/* Main Cta */}
								<button
									type={"button"}
									disabled={!selectedItem}
									className={cn("button fill success", !selectedItem && "disabled")}
									onClick={() => onConfirm(selectedItem)}
								>
									Prosegui
								</button>
							</div>
						</div>
					</>
				)}

				{/* Presets • empty */}
				{!isLoading && !itemsCount && (
					<>
						{/* Modal Body */}
						<div className={"main_body_modal"}>
							<EmptyBox
								icon="icon_75 default archive_entity"
								title="Nessun preset trovato"
								description={`Attualmente non sono disponibili preset per la tipologia ${organization?.organization_type?.name}`}
							/>

							{!!organizationTypeId && (
								<div>
									<button
										type={"button"}
										className="button fill primary w-full"
										onClick={() => onCreatePreset(organizationTypeId)}
									>
										Crea nuovo preset
									</button>
								</div>
							)}
						</div>
					</>
				)}
			</div>
		</ModalContainer>
	);
}
